/**
 * UsageWidget Component
 * Displays usage status with calculations used
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthProvider';
import { getUsageStats } from '../services/authService';

const UsageWidget = ({ className = '' }) => {
  const { user, isAuthenticated } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        const data = await getUsageStats();
        setStats(data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch usage stats:', err);
        setError('Failed to load usage status');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();

    // Refresh every 15s so admin-side limit changes propagate quickly.
    // Also refresh when the tab regains focus so a user coming back from
    // another tab sees the latest limit immediately.
    const interval = setInterval(fetchStats, 15000);
    const onFocus = () => fetchStats();
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return null;
  }

  // A compact, single-row meter that always fits inside the header (it used to be a
  // three-line card taller than the header itself, so it hung out of the bar).
  const shell = 'h-9 items-center gap-2.5 rounded-full border bg-card px-3 text-xs shadow-sm';

  if (loading) {
    return (
      <div className={`${shell} border-border ${className}`} aria-busy="true">
        <div className="h-2 w-24 animate-pulse rounded-full bg-muted" />
      </div>
    );
  }

  if (error || !stats) {
    return null; // the dashboard works without it; don't put an error box in the header
  }

  const isUnlimited = stats.unlimited === true;
  const used = stats.calculations_used;
  const limit = stats.calculations_limit;
  const pct = isUnlimited ? 0 : Math.min(100, (used / Math.max(1, limit)) * 100);
  const isExpired = !stats.valid;
  const isLow = !isUnlimited && stats.calculations_remaining <= 5;
  const tone = isExpired || pct >= 90 ? 'bg-destructive' : pct >= 70 ? 'bg-amber-500' : 'bg-primary';
  const title = isExpired
    ? 'Limit reached. Contact your admin for more access.'
    : isUnlimited
      ? `Unlimited plan · ${used} calculations used`
      : `${used} of ${limit} calculations used · ${stats.calculations_remaining} remaining`;

  return (
    <div
      className={`${shell} ${isExpired ? 'border-destructive/50' : 'border-border'} ${className}`}
      title={title}
      role="status"
      aria-label={title}
    >
      <span className="font-medium text-muted-foreground">Usage</span>
      {isUnlimited ? (
        <span className="font-semibold text-foreground">Unlimited</span>
      ) : (
        <>
          <span className="relative h-1.5 w-20 overflow-hidden rounded-full bg-muted" aria-hidden="true">
            <span className={`absolute inset-y-0 left-0 rounded-full ${tone}`} style={{ width: `${pct}%` }} />
          </span>
          <span className={`tabular-nums font-semibold ${isLow || isExpired ? 'text-destructive' : 'text-foreground'}`}>
            {used.toLocaleString()}<span className="font-normal text-muted-foreground"> / {limit.toLocaleString()}</span>
          </span>
        </>
      )}
      {isExpired && <span className="font-semibold text-destructive">Limit reached</span>}
    </div>
  );
};

export default UsageWidget;
